import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { api, dt, errMsg, exportCsv, rp } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function Reports() {
  const [tab, setTab] = useState("revenue");
  const [revenue, setRevenue] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [trx, setTrx] = useState([]);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [r, v, t, a] = await Promise.all([
          api.get("/reports/revenue"), api.get("/reports/vouchers"),
          api.get("/reports/transactions"), api.get("/audit"),
        ]);
        setRevenue(r.data); setVouchers(v.data); setTrx(t.data); setAudit(a.data);
      } catch (e) { toast.error(errMsg(e)); }
    })();
  }, []);

  const Exp = ({ name, rows }) => (
    <Button size="sm" variant="outline" className="rounded-full" onClick={() => exportCsv(name, rows)} data-testid={`export-${name}`}>
      <Download className="mr-1 h-3.5 w-3.5" /> Export CSV
    </Button>
  );

  const downloadXlsx = async () => {
    try {
      const res = await api.get("/reports/export.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `laporan-alphabill-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Laporan Excel diunduh");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Tabs value={tab} onValueChange={setTab} className="fade-up space-y-6">
      <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 rounded-xl bg-secondary/60 p-1">
        {[["revenue", "Pendapatan"], ["vouchers", "Voucher"], ["trx", "Transaksi Saldo"], ["audit", "Audit Trail"]].map(([v, l]) => (
          <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-lg px-4 py-2 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500/20 data-[state=active]:to-violet-600/20">{l}</TabsTrigger>
        ))}
      </TabsList>

      <TabsContent value="revenue" className="space-y-4">
        <div className="flex justify-end gap-2">
          <Button size="sm" className="rounded-full bg-gradient-to-r from-emerald-500 to-cyan-600 text-white" onClick={downloadXlsx} data-testid="export-xlsx">
            <Download className="mr-1 h-3.5 w-3.5" /> Unduh Excel (.xlsx)
          </Button>
          <Exp name="pendapatan.csv" rows={revenue} />
        </div>
        <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card">
          <Table data-testid="revenue-table">
            <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Voucher</TableHead><TableHead>Pendapatan Voucher</TableHead><TableHead>FTTH</TableHead><TableHead>Total</TableHead></TableRow></TableHeader>
            <TableBody>
              {revenue.map((r) => (
                <TableRow key={r.date} className="font-mono text-xs">
                  <TableCell data-label="Tanggal">{r.date}</TableCell><TableCell data-label="Voucher">{r.voucher_count}</TableCell><TableCell data-label="Pendapatan Voucher">{rp(r.voucher_revenue)}</TableCell>
                  <TableCell data-label="FTTH">{rp(r.ftth_revenue)}</TableCell><TableCell data-label="Total" className="font-semibold">{rp(r.total)}</TableCell>
                </TableRow>
              ))}
              {!revenue.length && <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground cell-empty">Belum ada pendapatan</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="vouchers" className="space-y-4">
        <div className="flex justify-end"><Exp name="voucher.csv" rows={vouchers} /></div>
        <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card">
          <Table data-testid="vouchers-table">
            <TableHeader><TableRow><TableHead>Username</TableHead><TableHead>Password</TableHead><TableHead>Profile</TableHead><TableHead>Harga</TableHead><TableHead>Pemilik</TableHead><TableHead>Batch</TableHead><TableHead>Dibuat</TableHead></TableRow></TableHeader>
            <TableBody>
              {vouchers.slice(0, 300).map((v) => (
                <TableRow key={v.id} className="font-mono text-xs">
                  <TableCell data-label="Username">{v.username}</TableCell><TableCell data-label="Password">{v.password}</TableCell><TableCell data-label="Profile">{v.profile || "-"}</TableCell>
                  <TableCell data-label="Harga">{rp(v.price)}</TableCell><TableCell data-label="Pemilik">{v.owner_username}</TableCell><TableCell data-label="Batch">{v.batch || "-"}</TableCell><TableCell data-label="Dibuat">{dt(v.created_at)}</TableCell>
                </TableRow>
              ))}
              {!vouchers.length && <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground cell-empty">Belum ada voucher</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="trx" className="space-y-4">
        <div className="flex justify-end"><Exp name="transaksi.csv" rows={trx} /></div>
        <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card">
          <Table data-testid="trx-table">
            <TableHeader><TableRow><TableHead>User</TableHead><TableHead>Tipe</TableHead><TableHead>Nominal</TableHead><TableHead>Saldo Akhir</TableHead><TableHead>Catatan</TableHead><TableHead>Waktu</TableHead></TableRow></TableHeader>
            <TableBody>
              {trx.map((t) => (
                <TableRow key={t.id} className="font-mono text-xs">
                  <TableCell data-label="User">{t.username}</TableCell><TableCell data-label="Tipe">{t.type}</TableCell>
                  <TableCell data-label="Nominal" className={t.amount < 0 ? "text-red-400" : "text-emerald-400"}>{rp(t.amount)}</TableCell>
                  <TableCell data-label="Saldo Akhir">{rp(t.balance_after)}</TableCell><TableCell data-label="Catatan" className="max-w-[240px] truncate">{t.note}</TableCell><TableCell data-label="Waktu">{dt(t.created_at)}</TableCell>
                </TableRow>
              ))}
              {!trx.length && <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground cell-empty">Belum ada transaksi</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="audit" className="space-y-4">
        <div className="flex justify-end"><Exp name="audit.csv" rows={audit} /></div>
        <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card">
          <Table data-testid="audit-table">
            <TableHeader><TableRow><TableHead>User</TableHead><TableHead>Role</TableHead><TableHead>Aksi</TableHead><TableHead>Detail</TableHead><TableHead>Waktu</TableHead></TableRow></TableHeader>
            <TableBody>
              {audit.map((a) => (
                <TableRow key={a.id} className="font-mono text-xs">
                  <TableCell data-label="User">{a.username}</TableCell><TableCell data-label="Role">{a.role}</TableCell><TableCell data-label="Aksi">{a.action}</TableCell>
                  <TableCell data-label="Detail">{a.detail}</TableCell><TableCell data-label="Waktu">{dt(a.created_at)}</TableCell>
                </TableRow>
              ))}
              {!audit.length && <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground cell-empty">Belum ada log</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </TabsContent>
    </Tabs>
  );
}
